'use client'

import { ProductReviewsFilterDesktop } from '@/app/[locale]/product/[slug]/_components/product-reviews/filter/filter-desktop'
import { ReviewsHeaderList } from '@/app/[locale]/product/[slug]/_components/product-reviews/product-reviews-desktop/reviews-header-list'
import { ProductReviewsItem } from '@/app/[locale]/product/[slug]/_components/product-reviews/product-reviews-item'
import { ProductReviewsRating } from '@/app/[locale]/product/[slug]/_components/product-reviews/rating'
import { Pagination } from '@/components/ui/pagination'
import { useScrollToHashSection } from '@/hooks/common/use-scroll-to-hash-section'
import { useReview } from '@/hooks/react-query/reviews/use-review'
import { PaginatedReviewResponse } from '@/types/api'
import { ReviewItem, ReviewResponse } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import { ProductReviewsSizeFitDistribution } from '../size-fit-distribution'
import { ProductReviewsEmpty } from '../reviews-empty'

type Props = {
  productId: string
  reviewAvg: number
  reviewCount: number
}

export const ProductReviewsDesktop = ({
  productId,
  reviewAvg,
  reviewCount,
}: Props) => {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<string>('')
  const [keywords, setKeywords] = useState<string>('')
  const t = useTranslations('product_detail.title')
  const sectionRef = useRef<HTMLElement>(null)

  const { data, isLoading } = useReview(productId, {
    page,
    limit: 10,
    filter,
    keywords,
  })

  useScrollToHashSection(sectionRef, '#product-reviews', !isLoading)

  // Cast to correct type
  const reviewData = data as PaginatedReviewResponse<ReviewResponse> | undefined
  const reviews = reviewData?.data?.list || []
  const totalPages = reviewData?.last_page || 1
  const totalItems = reviewData?.total || 0

  const scrollToReviews = () => {
    const reviewsSection = document.getElementById('product-reviews')
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleFilterChange = (value: string) => {
    setFilter(value)
    setPage(1) // Reset to first page when filter changes
    scrollToReviews()
  }

  const handleSearchChange = (value: string) => {
    setKeywords(value)
    setPage(1) // Reset to first page when search changes
  }

  return (
    <section
      ref={sectionRef}
      className='flex gap-24 bg-neutral-100 px-4 py-10 pt-20 max-lg:hidden lg:px-15 lg:py-14'
      id='product-reviews'
    >
      <div className='sticky top-32 h-full basis-1/5'>
        <h2 className='mb-12 font-criteria text-4xl font-medium uppercase leading-[47px] text-neutral-900'>
          {t.rich('product_reviews_desktop.title', {
            newline: () => <br />,
          })}
        </h2>
        <ProductReviewsFilterDesktop
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
        />
      </div>
      {reviewCount === 0 ? (
        <ProductReviewsEmpty className='flex-1' />
      ) : (
        <div className='flex-1'>
          <div className='mb-8 flex items-center justify-between pr-[102px]'>
            <ProductReviewsRating
              reviewAvg={reviewAvg}
              reviewCount={reviewCount}
            />
            <ProductReviewsSizeFitDistribution
              bodyFit={reviewData?.data?.body_fit || []}
            />
          </div>
          <ReviewsHeaderList
            onFilterChange={handleFilterChange}
            currentPage={page}
            totalItems={totalItems}
            itemsPerPage={10}
          />
          <div
            className='mt-5 space-y-3'
            role='list'
            aria-label='Product reviews list'
          >
            {reviews?.map((review: ReviewItem) => (
              <ProductReviewsItem key={review._id} review={review} />
            ))}
          </div>
          <Pagination
            totalPages={totalPages}
            page={page}
            onPageChange={setPage}
            wrapperClassName='mt-6 justify-end'
            className='gap-3'
          />
        </div>
      )}
    </section>
  )
}
